import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import get from 'lodash/get';
import set from 'lodash/set';
import { compose } from 'redux';
import { Tab } from 'nallian-shared-ui/lib/components';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { generateErrorsForRequiredFields } from '../../../app/lib/validationHelper';
import appInstallationsService from '../../services/appInstallations/appInstallationsService';
import appInstallationService from '../../services/appInstallations/appInstallationService';
import { COMMUNITY_APP_FORM_NAME } from '../../constants';
import CommunityAppDetails from './tabs/CommunityAppDetails';
import CommunityAppFeatures from './tabs/CommunityAppFeatures';
import { isValidUrl } from '../../../../app/helpers/isValidUrl';

const REQUIRED_FIELDS = ['appInstallation.id'];

// validates if it's a valid community app feature URL. Must be a valid
const isValidCafUrl = url => {
  if (url.includes('[EnvironmentUrl]') || isValidUrl(url)) return true;

  return false;
};

const validate = (values, { t }) => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  (values?.communityAppFeatures || []).forEach((caf, i) => {
    if (!caf.url) return null;
    if (!isValidCafUrl(caf.url)) {
      set(errors, `communityAppFeatures[${i}].url`, t('invalidUrlCommuntiyAppFeature'));
    }
    return null;
  });

  return errors;
};

const CommunityAppDetail = ({
  dispatchAppInstallationGet,
  appInstallationFetch,
  dispatchAppInstallationsGet,
  appInstallationsFetch,
  submitProcessing,
  dataLoading,
  data,
  returnRoute,
  onSubmit,
  header,
  readOnly,
  editMode,
}) => {
  const { t } = useTranslation();
  const { actionId } = useParams();

  const [activeIndex, setActiveIndex] = useState(0);

  const communityApps = useSelector(state => get(state, 'repository.communityApps.value', []));

  const appInstallationId = useSelector(state =>
    formValueSelector(COMMUNITY_APP_FORM_NAME)(state, 'appInstallation.id')
  );

  const appOptions = useMemo(
    () =>
      (appInstallationsFetch?.value || [])
        .filter(a => !communityApps.find(ca => ca.appInstallation?.tag === a.tag))
        .map(a => ({
          key: a.id,
          value: a.id,
          text: `${a.app?.name} - ${a.name}`,
          appId: a.app?.id,
        })),
    [communityApps, appInstallationsFetch]
  );

  const appOption = useMemo(() => appOptions.find(a => a.value === appInstallationId), [
    appOptions,
    appInstallationId,
  ]);

  useEffect(() => dispatchAppInstallationsGet(t), [dispatchAppInstallationsGet, t]);

  const appId = data?.appInstallation?.app?.id || appOption?.appId;

  useEffect(() => {
    if (appId && appInstallationId) {
      dispatchAppInstallationGet(appId, appInstallationId, t);
    }
  }, [t, appInstallationId, appId, dispatchAppInstallationGet]);

  const handleSetActiveIndex = useCallback((_, { activeIndex: index }) => setActiveIndex(index), [
    setActiveIndex,
  ]);

  const panes = useMemo(
    () => [
      {
        menuItem: 'Details',
        render: () => (
          <CommunityAppDetails
            data={data}
            appOptions={appOptions}
            submitProcessing={submitProcessing}
            dataLoading={dataLoading}
            actionId={actionId}
            readOnly={readOnly}
          />
        ),
      },
      appInstallationId
        ? {
            menuItem: 'Features',
            render: () => (
              <CommunityAppFeatures
                submitProcessing={submitProcessing}
                appInstallationFetch={appInstallationFetch}
                readOnly={readOnly}
                editMode={editMode}
              />
            ),
          }
        : undefined,
    ],
    [
      appInstallationId,
      actionId,
      appOptions,
      data,
      dataLoading,
      submitProcessing,
      appInstallationFetch,
      readOnly,
      editMode,
    ]
  );

  return (
    <FormModal
      closeIcon
      header={header}
      dataLoading={dataLoading || appInstallationFetch.pending || appInstallationsFetch.pending}
      submitProcessing={submitProcessing}
      onSubmit={onSubmit}
      data={data}
      t={t}
      validate={validate}
      form={COMMUNITY_APP_FORM_NAME}
      cancelRoute={returnRoute}
      cancelButtonContent={t(readOnly ? 'close' : 'cancel')}
      saveButtonIcon="save"
      saveButtonContent={t('save')}
      noSaveButton={readOnly}
    >
      <Tab
        activeIndex={activeIndex}
        renderActiveTabOnly={false}
        onTabChange={handleSetActiveIndex}
        panes={panes}
      />
    </FormModal>
  );
};

CommunityAppDetail.propTypes = {
  dispatchAppInstallationGet: PropTypes.func.isRequired,
  appInstallationFetch: PropTypes.object.isRequired,
  dispatchAppInstallationsGet: PropTypes.func.isRequired,
  appInstallationsFetch: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  editMode: PropTypes.bool,
  dataLoading: PropTypes.bool,
  submitProcessing: PropTypes.bool,
  header: PropTypes.string.isRequired,
  returnRoute: PropTypes.string.isRequired,
  data: PropTypes.object,
};

CommunityAppDetail.defaultProps = {
  dataLoading: false,
  submitProcessing: false,
  readOnly: false,
  editMode: false,
  data: {},
};

export default compose(appInstallationsService, appInstallationService)(CommunityAppDetail);
